import { getRouteApi } from '@tanstack/react-router'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import AddIcon from '@/assets/icons/add.svg?react'
import DeleteIcon from '@/assets/icons/delete.svg?react'
import EditIcon from '@/assets/icons/edit.svg?react'
import Button from '@/components/Button/Button'
import Table, { type TableAction } from '@/components/Table/Table'
import { TABLE_CELL_WIDTH } from '@/components/Table/table-cell-width'
import CommentListExpandedRow from '@/components/common/CommentListExpandedRow/CommentListExpandedRow'
import CommentModal from '@/components/common/CommentModal/CommentModal'
import ExportButton from '@/components/common/ExportButton'
import Filters, { type Filter } from '@/components/common/Filters/Filters'
import GradebookButton from '@/components/common/GradebookButton'
import { RemoveCommentModal } from '@/components/common/RemoveCommentModal'
import DEFAULT_PAGE_SIZE from '@/constants/default-page-size'
import { useColumns } from '@/hooks/useColumns'
import useCommonCommentFilters from '@/hooks/useCommonCommentFilters'
import useCommonCommentsColumn from '@/hooks/useCommonCommentsColumn'
import { usePagination } from '@/hooks/usePagination'
import { useScreenResolution } from '@/hooks/useScreenResolution'
import useCommentsDownload from '@/mutations/useCommentsDowload'
import useComments, { type Comment } from '@/queries/useComments'
import useCommentsCount from '@/queries/useCommentsCount'
import useSemesters from '@/queries/useSemesters'
import useStudentCoursesOptions from '@/queries/useStudentCoursesOptions'
import useAuthStore from '@/store/useAuthStore'
import { checkIfFilterSelected } from '@/utils/check-if-filters-selected'
import { getFilterValue } from '@/utils/get-filter-value'

import { type CommentFiltersKeys } from '../constants/comment-filters'
import useStudent from '../queries/useStudent'

const routeApi = getRouteApi(
  '/_auth/students-and-classes/students/$studentId/comments'
)

const CommentsView = () => {
  const { t } = useTranslation(['students', 'common'])
  const { studentId } = routeApi.useParams()
  const search = routeApi.useSearch()
  const navigate = routeApi.useNavigate()

  const [isModalOpen, setIsModalOpen] = useState(false)
  const [currentComment, setCurrentComment] = useState<Comment | null>(null)
  const [isOpenRemoveCommentModal, setIsOpenRemoveCommentModal] =
    useState(false)

  const { isMobile } = useScreenResolution()

  const user = useAuthStore(store => store.user)

  const { data: student } = useStudent(studentId)

  const isStudentArchived = student?.status === 'archived'

  const { data: semesters, isLoading: isSemesterLoading } = useSemesters()

  const {
    data: comments,
    isLoading,
    refetch: refetchComments
  } = useComments({
    studentsId: [studentId],
    ...search
  })

  const {
    data: commentsCount,
    isLoading: isCommentsCountLoading,
    refetch: refetchCommentsCount
  } = useCommentsCount({
    studentsId: [studentId],
    semester: search.semester
  })

  const handleAddComment = () => {
    refetchComments()
    refetchCommentsCount()
    refetchAddedByOptions()
  }

  const { data: courses, isLoading: isCoursesLoading } =
    useStudentCoursesOptions({
      studentId,
      semester: search.semester
    })

  const pagination = usePagination(routeApi)

  const { commonCommentsColumns } = useCommonCommentsColumn()

  const { columns, ...manageColumns } = useColumns<Comment>(
    [
      {
        key: 'source',
        label: t('header.source'),
        width: isMobile ? TABLE_CELL_WIDTH.LG : undefined,
        render: (value: Comment) =>
          value.group?.course.displayName ?? t('text.other')
      },
      ...commonCommentsColumns,
      {
        key: 'gradebook',
        render: (value: Comment) =>
          value.group ? (
            <GradebookButton
              courseId={value.group.course.id}
              groupId={value.group.id}
              semesterId={semesters?.currentSemester?.id}
            />
          ) : null,
        width: TABLE_CELL_WIDTH.ACTIONS,
        filled: true,
        alwaysVisible: true
      }
    ],
    { routeApi }
  )

  const handleChangeFilter = (
    key: CommentFiltersKeys,
    value?: string | string[] | Date
  ) => {
    navigate({
      search: previousValue => ({
        ...previousValue,
        page: 1,
        [key]: getFilterValue(value)
      })
    })
  }

  const handleSemesterChange = (value: string) => {
    navigate({
      search: {
        semester: value,
        page: 1,
        pageSize: DEFAULT_PAGE_SIZE
      }
    })
  }

  const handleClearAll = () => {
    navigate({
      search: {
        semester: semesters?.currentSemester.id || '',
        page: 1,
        pageSize: DEFAULT_PAGE_SIZE
      }
    })
  }

  const { commonCommentsFilters, refetchAddedByOptions } =
    useCommonCommentFilters({
      ...search,
      studentId,
      semester: semesters?.getSemester(search.semester),
      onChangeFilter: handleChangeFilter
    })

  const filters: Filter[] = [
    {
      label: t('label.semester'),
      variant: 'semester',
      size: 'wide',
      filterProps: {
        id: 'semester',
        loading: isSemesterLoading,
        options: semesters?.semestersFromCurrentYearOptions || [],
        semesterList: semesters?.list || [],
        value: search.semester,
        onChange: handleSemesterChange
      }
    },
    {
      label: t('label.course'),
      variant: 'multiselect',
      filterProps: {
        id: 'course',
        multiple: true,
        loading: isCoursesLoading,
        options: courses?.options || [],
        value: search.coursesId,
        placeholder: t('label.course'),
        onChange: value => handleChangeFilter('coursesId', value)
      }
    },
    ...commonCommentsFilters
  ]

  const isSomeFilterSelected = checkIfFilterSelected(filters, ['semester'])

  const actions: (row: Comment) => TableAction<Comment>[] = row =>
    [
      {
        icon: <EditIcon />,
        text: t('button.edit'),
        hidden: row.addedBy.id !== user?.id && !user?.isSuperAdmin,
        onClick: (item: Comment) => {
          setCurrentComment(item)
          setIsModalOpen(true)
        }
      },
      {
        icon: <DeleteIcon />,
        text: t('button.remove'),
        variantAction: 'danger' as const,
        hidden: row.addedBy.id !== user?.id && !user?.isSuperAdmin,
        onClick: (item: Comment) => {
          setCurrentComment(item)
          setIsOpenRemoveCommentModal(true)
        }
      }
    ].filter(item => !item.hidden)

  const closeModal = () => {
    setIsModalOpen(false)
    setCurrentComment(null)
  }

  const selectedSemester = semesters?.getSemester(search.semester)

  const canChangeComments = selectedSemester?.status !== 'archived'

  const download = useCommentsDownload({
    ...search,
    studentsId: [studentId],
    columnsKey: manageColumns.activeColumnKeys.filter(
      item => item !== 'gradebook'
    )
  })

  return (
    <>
      <Filters
        filters={filters}
        onClearAll={handleClearAll}
        disabledClearAllButton={!isSomeFilterSelected}
        actionsRender={
          <>
            <ExportButton {...download} />
            {!isStudentArchived ? (
              <CommentModal
                semester={search.semester}
                open={isModalOpen}
                onOpenChange={value => {
                  if (value) setIsModalOpen(value)
                  else closeModal()
                }}
                onUpdate={handleAddComment}
                commentId={currentComment?.id}
                studentId={studentId}
                trigger={
                  <Button icon={<AddIcon />} disabled={!canChangeComments}>
                    {t('common:button.add-comment')}
                  </Button>
                }
              />
            ) : null}
          </>
        }
        manageColumns={manageColumns}
      />
      <Table
        id="comments-table"
        data={comments?.list || []}
        isLoading={isLoading || isCommentsCountLoading}
        columns={columns}
        actions={canChangeComments && !isStudentArchived ? actions : []}
        count={comments?.count}
        totalCount={commentsCount}
        {...pagination}
        expandedRowRender={record => (
          <CommentListExpandedRow comment={record} />
        )}
        caption={t('header.comments')}
      />

      {currentComment ? (
        <RemoveCommentModal
          open={isOpenRemoveCommentModal}
          onOpenChange={setIsOpenRemoveCommentModal}
          commenId={currentComment.id}
          onSuccess={refetchComments}
        />
      ) : null}
    </>
  )
}

export default CommentsView
